import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import {
  BulkUpdatePropertyContentSourcesPayload,
  PropertyContentSourcesResponse,
  UpdatePropertyContentSourcesPayload,
  bulkUpdatePropertyContentSources,
  getPropertyContentSources,
  updatePropertyContentSources,
} from '~/services/AccommodationService';

export const propertyContentSourcesQueryKeys = {
  all: ['propertyContentSources'] as const,
  detail: (propertyId: string) => [...propertyContentSourcesQueryKeys.all, 'detail', propertyId] as const,
};

interface UseGetPropertyContentSourcesParams {
  propertyId: string;
  enabled?: boolean;
}

export function useGetPropertyContentSources({ propertyId, enabled = true }: UseGetPropertyContentSourcesParams) {
  return useQuery({
    queryKey: propertyContentSourcesQueryKeys.detail(propertyId),
    queryFn: async ({ signal }) => {
      const response = await getPropertyContentSources({ propertyId }, { signal });
      return response.result;
    },
    enabled: enabled && !!propertyId,
    refetchOnWindowFocus: false,
  });
}

interface UseUpdatePropertyContentSourcesParams {
  propertyId: string;
  data: UpdatePropertyContentSourcesPayload;
}

export function useUpdatePropertyContentSources() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ propertyId, data }: UseUpdatePropertyContentSourcesParams) => {
      const response = await updatePropertyContentSources({ propertyId }, data);
      return response.result;
    },
    onSuccess: (data, variables) => {
      queryClient.setQueryData<PropertyContentSourcesResponse>(
        propertyContentSourcesQueryKeys.detail(variables.propertyId),
        data,
      );
    },
  });
}

interface UseBulkUpdatePropertyContentSourcesParams {
  propertyId: string;
  data: BulkUpdatePropertyContentSourcesPayload;
}

export function useBulkUpdatePropertyContentSources() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ propertyId, data }: UseBulkUpdatePropertyContentSourcesParams) => {
      const response = await bulkUpdatePropertyContentSources({ propertyId }, data);
      return response.result;
    },
    onSuccess: (data, variables) => {
      queryClient.setQueryData<PropertyContentSourcesResponse>(
        propertyContentSourcesQueryKeys.detail(variables.propertyId),
        data,
      );
    },
  });
}
